/**
 * GroupsChip — the Groups icon beside the player icon (§ F208).
 *
 * Jake: *"Need a 'Groups' icon in the header by the user icon"* and *"I want to
 * send group invites that people get when they login."* The icon opens My
 * Groups and wears a count of invites waiting. The first time a signed-in
 * player lands with invites waiting (once per account per page load), they
 * appear in a pop-up in the site's own modal style — Join / No thanks.
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UsersRound, X } from "lucide-react";
import { fetchInvites, INVITES_CHANGED, type GroupInvite } from "../lib/account";
import { usePlayer } from "../lib/usePlayer";
import { ReceivedInvites } from "./ReceivedInvites";

const prompted = new Set<string>();

export function GroupsChip() {
  const { player } = usePlayer();
  const navigate = useNavigate();
  const location = useLocation();
  const [invites, setInvites] = useState<GroupInvite[]>([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!player) return;
    fetchInvites()
      .then((list) => {
        setInvites(list);
        if (list.length > 0 && !prompted.has(player.id) && location.pathname !== "/groups") {
          prompted.add(player.id);
          setOpen(true);
        }
        if (list.length === 0) setOpen(false);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id]);

  useEffect(() => {
    load();
    window.addEventListener(INVITES_CHANGED, load);
    return () => window.removeEventListener(INVITES_CHANGED, load);
  }, [load]);

  if (!player) return null;

  return (
    <>
      <button
        type="button"
        data-testid="chrome-groups"
        aria-label={invites.length ? `Groups — ${invites.length} invite${invites.length === 1 ? "" : "s"} waiting` : "Groups"}
        onClick={() => navigate("/groups")}
        className="gtw-hicon relative shrink-0 bg-white ink-border rounded-block btn-press flex items-center justify-center"
      >
        <UsersRound className="w-5 h-5" strokeWidth={2.5} />
        {invites.length > 0 && (
          <span
            data-testid="chrome-groups-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-secondary text-white text-[11px] font-extrabold leading-[18px] text-center ink-border"
          >
            {invites.length}
          </span>
        )}
      </button>

      {open && invites.length > 0 && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Group invites"
          data-testid="invites-prompt"
          onClick={() => setOpen(false)}
        >
          <div className="w-full max-w-xs flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
            <ReceivedInvites invites={invites} onAnswered={load} />
            <button
              type="button"
              data-testid="invites-prompt-later"
              onClick={() => setOpen(false)}
              className="w-full min-h-[40px] px-4 bg-white font-heading font-extrabold uppercase tracking-wide text-sm ink-border rounded-block btn-press flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" strokeWidth={2.5} /> Later
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default GroupsChip;
